import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import {
  useOperators,
  useCreateOperator,
  useUpdateOperator,
  useDeleteOperator,
} from '../../lib/operators-api';
import type { OperatorResponse } from '@produzionejb/types';

export function OperatorsListPage() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<OperatorResponse | null>(null);

  const list = useOperators({ pageSize: 200 });
  const items = list.data?.items ?? [];
  const reali = items.filter((o) => !o.isLegacy);
  const legacy = items.filter((o) => o.isLegacy);

  function handleEdit(op: OperatorResponse) {
    setEditing(op);
    setShowForm(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('operators.list.title')}</h2>
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          {t('operators.list.newButton')}
        </Button>
      </div>

      {showForm && (
        <OperatorForm
          editing={editing}
          onDone={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('operators.list.realOperators', { count: reali.length })}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {list.isLoading && <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>}
          {!list.isLoading && reali.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('operators.list.empty')}</p>
          )}
          {reali.length > 0 && <OperatorTable items={reali} onEdit={handleEdit} />}
        </CardContent>
      </Card>

      {legacy.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-slate-600">
              {t('operators.list.legacyOperators', { count: legacy.length })}
            </CardTitle>
            <p className="mt-1 text-xs text-slate-500">{t('operators.list.legacyDisclaimer')}</p>
          </CardHeader>
          <CardContent className="p-0">
            <OperatorTable items={legacy} onEdit={handleEdit} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function OperatorTable({
  items,
  onEdit,
}: {
  items: OperatorResponse[];
  onEdit: (op: OperatorResponse) => void;
}) {
  const { t } = useTranslation();
  const deleteMut = useDeleteOperator();

  async function handleDelete(op: OperatorResponse) {
    if (!window.confirm(t('operators.list.confirmDelete', { name: op.fullName }))) return;
    try {
      await deleteMut.mutateAsync(op.id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  return (
    <table className="w-full text-sm">
      <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
        <tr>
          <th className="px-4 py-2">{t('operators.fields.code')}</th>
          <th className="px-4 py-2">{t('operators.fields.fullName')}</th>
          <th className="px-4 py-2">{t('operators.fields.active')}</th>
          <th className="px-4 py-2">{t('operators.fields.hiredAt')}</th>
          <th className="px-4 py-2">{t('operators.fields.notes')}</th>
          <th className="px-4 py-2 text-right">{t('common.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((op) => (
          <tr key={op.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-2 font-mono text-xs">{op.code}</td>
            <td className="px-4 py-2 font-medium">{op.fullName}</td>
            <td className="px-4 py-2">
              {op.active ? (
                <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
                  {t('common.yes')}
                </span>
              ) : (
                <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600">
                  {t('common.no')}
                </span>
              )}
            </td>
            <td className="px-4 py-2 text-slate-600">
              {op.hiredAt ? new Date(op.hiredAt).toLocaleDateString('it-IT') : '—'}
            </td>
            <td className="px-4 py-2 text-xs text-slate-500">{op.notes ?? '—'}</td>
            <td className="px-4 py-2 text-right">
              <Button variant="ghost" size="sm" onClick={() => onEdit(op)}>
                {t('common.edit')}
              </Button>
              {!op.isLegacy && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => void handleDelete(op)}
                  className="ml-2"
                >
                  {t('common.delete')}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OperatorForm({
  editing,
  onDone,
}: {
  editing: OperatorResponse | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState(editing?.code ?? '');
  const [fullName, setFullName] = useState(editing?.fullName ?? '');
  const [active, setActive] = useState(editing?.active ?? true);
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const createMut = useCreateOperator();
  const updateMut = useUpdateOperator(editing?.id ?? '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editing) {
        await updateMut.mutateAsync({
          code: code.trim() !== editing.code ? code.trim() : undefined,
          fullName: fullName.trim() !== editing.fullName ? fullName.trim() : undefined,
          active: active !== editing.active ? active : undefined,
          notes: notes.trim() !== (editing.notes ?? '') ? notes.trim() : undefined,
        });
      } else {
        await createMut.mutateAsync({
          code: code.trim(),
          fullName: fullName.trim(),
          active,
          notes: notes.trim() || null,
        });
      }
      onDone();
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  const busy = createMut.isPending || updateMut.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {editing ? t('operators.form.editTitle') : t('operators.form.newTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-slate-500">{t('operators.fields.code')}</label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Andrei" required maxLength={32} />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('operators.fields.fullName')}</label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Andrei Petrescu"
              required
              maxLength={120}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('operators.fields.notes')}</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              {t('operators.fields.active')}
            </label>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? t('common.saving') : editing ? t('common.save') : t('common.create')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
